import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
} from "@reduxjs/toolkit/query/react";
import { HTTP_STATUS, RESPONSE_MESSAGES, API_ENDPOINTS } from "@/constants";
import { logout } from "@/features/auth/authSlice";
import { toastService } from "./toast";
import { RootState } from "@/store";
import type { FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { ApiResponse } from "@/types";

// Type guard to check if an object is ApiResponse
function isApiResponse(obj: unknown): obj is ApiResponse {
  return typeof obj === "object" && obj !== null && "success" in obj;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL,
  prepareHeaders: (headers, { getState }) => {
    const accessToken = (getState() as RootState).auth.accessToken;
    if (accessToken) headers.set("authorization", `Bearer ${accessToken}`);

    // Bypass ngrok browser warning
    headers.set("ngrok-skip-browser-warning", "true");

    return headers;
  },
});

const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  // Handle Errors
  if (result.error) {
    const status = result.error.status;
    const url = typeof args === "string" ? args : args.url;
    // Check if it's a login request (Admin or App) - simple check for ending with /login is robust enough here
    // or we can use specific endpoints from constants
    const isLoginRequest =
      url.endsWith(API_ENDPOINTS.ADMIN.LOGIN) ||
      url.endsWith(API_ENDPOINTS.APP.LOGIN);

    if (status === HTTP_STATUS.UNAUTHORIZED && !isLoginRequest) {
      api.dispatch(logout());
      toastService.error(RESPONSE_MESSAGES.ERROR.SESSION_EXPIRED);
    } else {
      // Prioritize API returned message -> standard generic error
      let errorMessage: string = RESPONSE_MESSAGES.ERROR.GENERIC;

      if (result.error.data && typeof result.error.data === "object") {
        const errorData = result.error.data as Record<string, unknown>;

        // Check for 'error' property
        if ("error" in errorData) {
          const err = errorData.error;
          errorMessage = typeof err === "string" ? err : JSON.stringify(err);
        }
        // Fallback check for 'message'
        else if ("message" in errorData) {
          const msg = errorData.message;
          errorMessage = typeof msg === "string" ? msg : JSON.stringify(msg);
        }
      }

      toastService.error(errorMessage);
    }
  }

  // Handle Success
  if (isApiResponse(result.data) && result.data.message) {
    toastService.success(result.data.message);
  }

  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery,
  tagTypes: ["Users", "Avatars", "Words", "Groups"],
  endpoints: () => ({}),
});
